import React, { useState } from 'react';
import { Plus, Edit3 } from 'lucide-react';
import type { Assumption, AssumptionScope } from '../../types';
import { useAssumptions } from '../../hooks/useAssumptions';
import { useForecastV3 } from '../../context/ForecastV3Context';
import { ListSkeleton } from '../shared/Skeleton';
import { EmptyState } from '../shared/EmptyState';

interface AssumptionCardProps {
  assumption: Assumption;
  onEdit?: (assumption: Assumption) => void;
}

function AssumptionCard({ assumption, onEdit }: AssumptionCardProps) {
  return (
    <div
      className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-sm transition-all cursor-pointer"
      onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--lodge-orange-200, var(--fv3-accent-line, #fed7aa))'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = ''; }}
      onClick={() => onEdit?.(assumption)}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-medium text-slate-800">{assumption.name}</h3>
          <div className="flex items-center gap-2 mt-1">
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded ${
                assumption.type === 'Scalar'
                  ? 'bg-purple-50 text-purple-600'
                  : 'bg-teal-50 text-teal-600'
              }`}
            >
              {assumption.type}
            </span>
            <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">
              {assumption.scope}
            </span>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-sm text-slate-800">{assumption.value}</div>
          <div className="text-xs text-slate-400">Used by {assumption.usedBy} accounts</div>
        </div>
      </div>
      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
        <div className="flex items-center gap-1.5 text-xs text-slate-400">
          <span
            className={`px-1.5 py-0.5 rounded ${
              assumption.source === 'PMS'
                ? 'bg-emerald-50 text-emerald-600'
                : assumption.source === 'RMS'
                ? 'bg-blue-50 text-blue-600'
                : assumption.source === 'Budget'
                ? 'bg-indigo-50 text-indigo-600'
                : 'bg-slate-100 text-slate-500'
            }`}
          >
            {assumption.source}
          </span>
          <span>Updated {assumption.lastUpdated}</span>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEdit?.(assumption);
          }}
          style={{ color: 'var(--lodge-orange-400)' }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-orange-500)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--lodge-orange-400)'; }}
        >
          <Edit3 size={14} />
        </button>
      </div>
    </div>
  );
}

export function AssumptionsPage() {
  const { state } = useForecastV3();
  const { orgId, locationId } = state;
  const [activeTab, setActiveTab] = useState<AssumptionScope | 'all'>('Global');

  const { assumptions, isLoading, error } = useAssumptions({
    orgId: orgId ?? 0,
    locationId: locationId ?? 0,
    enabled: !!orgId && !!locationId,
  });

  const filteredAssumptions = assumptions.filter((a) => {
    if (activeTab === 'all') return true;
    return a.scope === activeTab;
  });

  return (
    <div className="flex-1 p-6 overflow-auto">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">Assumptions Manager</h2>
            <p className="text-sm text-slate-500 mt-1">
              Configure drivers and inputs that power your forecasts
            </p>
          </div>
          <button
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors"
            style={{ background: 'var(--lodge-orange-400)', color: '#ffffff', boxShadow: 'var(--shadow-orange)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-500)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-400)'; }}
          >
            <Plus size={16} />
            New Assumption
          </button>
        </div>

        {/* Scope Tabs */}
        <div className="flex items-center gap-1 mb-6 bg-slate-100 rounded-lg p-1 w-fit">
          {(['Global', 'Property', 'Department'] as AssumptionScope[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm font-medium rounded-md capitalize transition-colors ${
                activeTab === tab
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ListSkeleton rows={3} />
            <ListSkeleton rows={3} />
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="p-4 bg-red-50 rounded-xl border border-red-100 text-red-600 mb-6">
            Error loading assumptions: {error.message}
          </div>
        )}

        {/* Assumptions Grid */}
        {!isLoading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredAssumptions.map((assumption) => (
              <AssumptionCard
                key={assumption.id}
                assumption={assumption}
                onEdit={(a) => console.log('Edit assumption:', a)}
              />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && filteredAssumptions.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200">
            <EmptyState
              type="assumptions"
              description={`No ${activeTab === 'Global' ? 'global' : activeTab === 'Property' ? 'property-level' : 'department-level'} assumptions configured.`}
              action={{
                label: 'Add assumption',
                onClick: () => console.log('Create assumption'),
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default AssumptionsPage;
